"use client";

/**
 * useCiteproc
 * - 统一封装 citeproc-js 的引用与参考文献格式化
 * - 本地加载 CSL 样式与 locale，并做内存缓存
 * - 参考文献输出返回 { map, order }，order 与 citeproc 排序保持一致
 * 
 * 注意：此 hook 需要 citeproc 库，请确保已安装：
 * - 将 citeproc 库放置在 lib/third-party/citeproc/ 目录下
 * - 或安装 npm 包：npm install citeproc
 */

import { useCallback, useState } from "react";
import CSL from "@/lib/third-party/citeproc";

export type SupportedStyleKey = "GB/T 7714-2015" | "apa" | "mla" | "chicago" | "ieee" | "ama" | "harvard";
export type SupportedLang = "zh-CN" | "en-US";

export type CslItem = Record<string, any> & {
  lang?: SupportedLang | string;
  id: string;
};

/** 业务语言 -> citeproc locale 文件名映射 */
export const LANG_NAME_MAP: Record<string, SupportedLang> = {
  "zh": "zh-CN",
  "en": "en-US",
};

/** 从 item 的 lang 解析为 SupportedLang，缺省时用 fallback */
function getItemLang(item: CslItem, fallback: SupportedLang): SupportedLang {
  const raw = item?.lang;
  if (raw === "zh-CN" || raw === "en-US") return raw as SupportedLang;
  if (raw && (LANG_NAME_MAP as Record<string, SupportedLang>)[String(raw)]) {
    return (LANG_NAME_MAP as Record<string, SupportedLang>)[String(raw)];
  }
  return fallback;
}

/** 基础引用样式列表（不包含语言特定的样式） */
const BASE_CITATION_FORMAT_OPTIONS = [{
  value: 'apa',
  label: "APA",
}, {
  value: 'mla',
  label: "MLA",
}, {
  value: 'chicago',
  label: "Chicago",
}, {
  value: 'ieee',
  label: "IEEE",
}, {
  value: 'ama',
  label: "AMA",
}, {
  value: 'harvard',
  label: "HARVARD",
}];

/** GB/T 7714-2015 选项（仅中文显示） */
const GB7714_OPTION = {
  value: 'GB/T 7714-2015',
  label: "GB/T 7714-2015",
};

/** 用于选择器展示的引用样式列表（默认包含 GB/T 7714-2015，用于向后兼容） */
export const CITATION_FORMAT_OPTIONS = [GB7714_OPTION, ...BASE_CITATION_FORMAT_OPTIONS];

/**
 * 根据语言获取引用格式选项
 * @param lang 语言代码（'zh' | 'en'），如果为中文则包含 GB/T 7714-2015，否则不包含
 */
export function getCitationFormatOptions(lang?: string) {
  if (lang === 'zh') {
    return [GB7714_OPTION, ...BASE_CITATION_FORMAT_OPTIONS];
  }
  return BASE_CITATION_FORMAT_OPTIONS;
}

/** 引用样式 key -> 对应的本地 csl 文件名（可通过 registerCitationStyles 动态扩展） */
export const STYLE_NAME_MAP: Record<string, string> = {
  "GB/T 7714-2015": "china-national-standard-gb-t-7714-2015-numeric",
  apa: "apa",
  mla: "modern-language-association",
  chicago: "chicago-author-date",
  ieee: "ieee",
  ama: "american-medical-association",
  harvard: "harvard-cite-them-right",
};

/**
 * 根据 API 返回的 citation_styles 配置动态注册样式到 STYLE_NAME_MAP
 * 使得后续新增的引用格式无需修改代码即可支持
 * @param styles - API 返回的引用格式配置 { [apiKey]: { style: "xxx.csl" } }
 * @param keyMapping - API key 到内部 styleKey 的映射（如 gbt7714-2015 -> GB/T 7714-2015）
 */
export function registerCitationStyles(
  styles: Record<string, { style: string }>,
  keyMapping?: Record<string, string>,
) {
  Object.entries(styles).forEach(([apiKey, { style }]) => {
    const styleName = style.replace(/\.csl$/, '');
    const mappedKey = keyMapping?.[apiKey] || apiKey;
    if (!STYLE_NAME_MAP[mappedKey]) {
      STYLE_NAME_MAP[mappedKey] = styleName;
    }
    if (apiKey !== mappedKey && !STYLE_NAME_MAP[apiKey]) {
      STYLE_NAME_MAP[apiKey] = styleName;
    }
  });
}

/** 本地静态资源根路径（public 下） */
const LOCAL_BASE = "/pkg/citeproc/citation-style-language";
/** 远程 CSL 样式仓库 CDN（本地文件缺失时兜底） */
const CSL_REMOTE_BASE = "https://cdn.jsdelivr.net/gh/citation-style-language/styles@master";

const STYLE_URL = (styleKey: string) => (
  `${LOCAL_BASE}/styles/${STYLE_NAME_MAP[styleKey] || styleKey}.csl`
);
const LOCALE_URL = (lang: SupportedLang) => {
  return `${LOCAL_BASE}/locales/locales-${lang}.xml`;
};

// 简单内存缓存：在会话内复用已加载的样式/locale 文本
const textCache = new Map<string, string>();

/**
 * 清理和规范化 CSL-JSON 数据，确保 citeproc 能正确处理
 * - 清理无效的页面范围格式
 * - 确保必需字段存在
 * - 处理 Chicago 格式的特殊要求
 */
function sanitizeCslItem(item: CslItem): CslItem {
  // 使用深拷贝避免修改只读对象
  const sanitized = JSON.parse(JSON.stringify(item));

  // 清理页面字段：Chicago 格式对页面范围格式要求严格
  // 如果 page 字段格式不正确，可能导致 page_mangler 报错
  if (sanitized.page) {
    const pageValue = sanitized.page;
    // 如果是字符串，确保格式正确（如 "123-145" 或 "123"）
    if (typeof pageValue === 'string') {
      // 移除可能导致问题的特殊字符
      sanitized.page = pageValue.replace(/[^\d\-\–\—,]/g, '').trim();
      // 如果清理后为空，移除该字段
      if (!sanitized.page) {
        delete sanitized.page;
      }
    } else if (Array.isArray(pageValue)) {
      // 如果是数组，确保格式正确
      const cleaned = pageValue.filter((p: any) => p != null && p !== '');
      if (cleaned.length > 0) {
        sanitized.page = cleaned;
      } else {
        delete sanitized.page;
      }
    }
  }

  // 确保 issued 字段格式正确
  if (sanitized.issued) {
    if (typeof sanitized.issued === 'object' && !Array.isArray(sanitized.issued)) {
      // 确保 date-parts 格式正确
      if (sanitized.issued['date-parts'] && Array.isArray(sanitized.issued['date-parts'])) {
        sanitized.issued['date-parts'] = sanitized.issued['date-parts'].filter(
          (part: any) => Array.isArray(part) && part.length > 0
        );
        if (sanitized.issued['date-parts'].length === 0) {
          delete sanitized.issued['date-parts'];
        }
      }
    }
  }
  return sanitized;
}

/** 加载样式/locale 文本，优先命中内存缓存，CSL 样式文件本地缺失时自动从远程 CDN 获取 */
async function fetchText(url: string): Promise<string> {
  if (textCache.has(url)) {
    return textCache.get(url)!;
  }

  const res = await fetch(url, { cache: "force-cache" });
  if (!res.ok) {
    const styleMatch = url.match(/\/styles\/(.+\.csl)$/);
    if (styleMatch) {
      const remoteUrl = `${CSL_REMOTE_BASE}/${styleMatch[1]}`;
      const remoteRes = await fetch(remoteUrl, { cache: "force-cache" });
      if (remoteRes.ok) {
        const text = await remoteRes.text();
        textCache.set(url, text);
        return text;
      }
    }
    throw new Error(`加载失败: ${url}`);
  }

  const text = await res.text();
  textCache.set(url, text);
  return text;
}

export interface CiteprocParams {
  style: SupportedStyleKey | (string & {});
  lang: SupportedLang;
  items: (CslItem | CslItem[])[];
}

export interface UseCiteprocResult {
  loading: boolean;
  error: string;
  formatCitationMap: (params: CiteprocParams) => Promise<Record<string, string>>;
  formatBibliography: (params: CiteprocParams) => Promise<{ map: Record<string, string>; order: string[] }>;
}

export function useCiteproc(): UseCiteprocResult {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  /**
   * 文内引用格式化
   * - 支持混合数组（同处一引）：CslItem 与 CslItem[]
   * - 返回 key->html：当输入为数组时，key 为多个 id 的逗号拼接
   */
  const formatCitationMap = useCallback(async ({ style, lang, items }: CiteprocParams): Promise<Record<string, string>> => {
    const result: Record<string, string> = {};
    setLoading(true);
    setError("");
    // try {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error("请输入有效的 CSL-JSON 数组");
    }

    // 处理混合数组：CslItem 和 CslItem[]
    const processedItems: CslItem[] = [];
    // 逐条/逐组构建 citation 集群，获取格式化结果
    for (const item of items) {
      if (Array.isArray(item)) {
        // 如果是数组
        processedItems.push(...item);
      } else if (item && typeof item.id === 'string' && item.id.trim().length > 0) {
        // 如果是单个对象
        processedItems.push(item);
      }
    }

    if (processedItems.length === 0) {
      throw new Error("数组中缺少有效的 id 字段");
    }

    // 根据每个 item 的 lang 收集需要加载的 locale，缺省用参数 lang
    const langsNeeded = new Set<SupportedLang>([lang]);
    processedItems.forEach((it) => langsNeeded.add(getItemLang(it, lang)));

    // 并行加载本地样式与所有需要的 locale
    const [styleStr, ...localeStrs] = await Promise.all([
      fetchText(STYLE_URL(style)),
      ...Array.from(langsNeeded).map((l) => fetchText(LOCALE_URL(l))),
    ]);
    const localeMap: Record<SupportedLang, string> = Object.fromEntries(
      Array.from(langsNeeded).map((l, i) => [l, localeStrs[i]])
    ) as Record<SupportedLang, string>;

    // 清理和规范化数据，避免 citeproc 处理时出错
    const sanitizedItems = processedItems.map(sanitizeCslItem);
    // 为每条 item 设置 CSL 标准字段 language，citeproc 据此调用 retrieveLocale 选用对应 locale
    sanitizedItems.forEach((it) => { it.language = getItemLang(it, lang); });
    const itemsById: Record<string, any> = Object.fromEntries(sanitizedItems.map((it) => [it.id, it]));
    const sys = {
      retrieveLocale: (requestedLang: string) => {
        const key = (requestedLang in LANG_NAME_MAP
          ? (LANG_NAME_MAP as Record<string, SupportedLang>)[requestedLang]
          : requestedLang) as SupportedLang;
        return localeMap[key] ?? localeMap[lang] ?? localeMap["en-US"] ?? "";
      },
      retrieveItem: (id: string) => {
        const item = itemsById[id];
        // 再次清理，确保返回的数据格式正确（特别是 Chicago 格式对页面范围的要求）
        return item ? sanitizeCslItem(item) : null;
      },
      getAbbreviation: () => undefined,
    } as any;

    const engine = new CSL.Engine(sys, styleStr, lang);
    engine.updateItems(Object.keys(itemsById));

    for (const item of items) {
      const id = Array.isArray(item) ? item.map((it) => it.id).join(',') : item.id;
      if (result[id]) continue;
      // 如果是数组，批量处理
      const citationItems = Array.isArray(item) ? item.map((it) => ({ id: it.id })) : [{ id: item.id }];
      const citationResult = engine.appendCitationCluster({
        citationItems,
        properties: {
          noteIndex: 0
        }
      }, [], []);
      result[id] = citationResult[0][1];
    }

    return result;
    // } catch (e: any) {
    //   setError(e?.message || String(e));
    //   return result;
    // } finally {
    //   setLoading(false);
    // }
  }, []);

  /**
   * 参考文献格式化
   * 返回：
   * - map：id -> HTML
   * - order：与 citeproc 的 params.entry_ids 一致（严格排序）
   */
  const formatBibliography = useCallback(async ({ style, lang, items }: CiteprocParams): Promise<{ map: Record<string, string>; order: string[] }> => {
    setLoading(true);
    setError("");
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error("请输入有效的 CSL-JSON 数组", { cause: items });
    }

    // 展平并按首次出现去重，确保 id 唯一且顺序稳定
    const validItems: CslItem[] = [];
    const seen = new Set<string>();
    const pushIfNotSeen = (it: CslItem) => {
      const id = it.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      validItems.push({ ...it, id });
    };
    for (const item of items) {
      if (Array.isArray(item)) {
        for (const it of item) {
          pushIfNotSeen(it as CslItem);
        }
      } else if (item && typeof item.id === 'string') {
        pushIfNotSeen(item);
      }
    }

    if (validItems.length === 0) {
      throw new Error("数组中缺少有效的 id 字段");
    }

    // 根据每个 item 的 lang 收集需要加载的 locale，缺省用参数 lang
    const langsNeeded = new Set<SupportedLang>([lang]);
    validItems.forEach((it) => langsNeeded.add(getItemLang(it, lang)));

    // 加载本地样式与所有需要的 locale
    const [styleStr, ...localeStrs] = await Promise.all([
      fetchText(STYLE_URL(style)),
      ...Array.from(langsNeeded).map((l) => fetchText(LOCALE_URL(l))),
    ]);
    const localeMap: Record<SupportedLang, string> = Object.fromEntries(
      Array.from(langsNeeded).map((l, i) => [l, localeStrs[i]])
    ) as Record<SupportedLang, string>;

    // 清理和规范化数据，避免 citeproc 处理时出错（特别是 Chicago 格式）
    const sanitizedItems = validItems.map(sanitizeCslItem);
    // 为每条 item 设置 CSL 标准字段 language，citeproc 据此调用 retrieveLocale 选用对应 locale
    sanitizedItems.forEach((it) => { it.language = getItemLang(it, lang); });
    const itemsById: Record<string, any> = Object.fromEntries(sanitizedItems.map((it) => [it.id, it]));
    const sys = {
      retrieveLocale: (requestedLang: string) => {
        const key = (requestedLang in LANG_NAME_MAP
          ? (LANG_NAME_MAP as Record<string, SupportedLang>)[requestedLang]
          : requestedLang) as SupportedLang;
        return localeMap[key] ?? localeMap[lang] ?? localeMap["en-US"] ?? "";
      },
      retrieveItem: (id: string) => {
        const item = itemsById[id];
        // 再次清理，确保返回的数据格式正确（特别是 Chicago 格式对页面范围的要求）
        return item ? sanitizeCslItem(item) : null;
      },
      getAbbreviation: () => undefined,
    } as any;

    const engine = new CSL.Engine(sys, styleStr, lang);
    engine.updateItems(sanitizedItems.map((it) => it.id));
    // 生成参考文献：[params, entries]
    // params.entry_ids 与 entries 一一对应，并按样式排序
    const bib = engine.makeBibliography();
    const params = (bib?.[0] || {}) as any;
    const entries = (bib?.[1] || []) as string[];
    const entryIds = (params?.entry_ids || []) as string[];
    // 按 entryIds 对齐 entries 构建映射；遍历时建议优先使用 order（entryIds）
    const bibliographyMap: Record<string, string> = {};
    for (let i = 0; i < entryIds.length; i += 1) {
      const id = entryIds[i];
      const html = entries[i];
      if (id && typeof html === 'string') {
        bibliographyMap[id] = html;
      }
    }
    return { map: bibliographyMap, order: entryIds };

  }, []);

  return {
    loading,
    error,
    formatCitationMap,
    formatBibliography,
  };
}

